"use client";

import { useEffect, useRef, useState } from "react";
import styles from "./index.module.css";
import ImageLoader from "../common/ImageLoader";

const AUTO_SLIDE_INTERVAL = 20000;
const SUB_SLIDE_INTERVAL = 8000;

export default function MiniCarousel({
  categories = [],
  setSearchTerm,
  imgRes = "600",
  zoomImgRes = "850",
}) {
  const [subIndexes, setSubIndexes] = useState({});
  const [isPaused, setIsPaused] = useState(false);

  const wrapperRef = useRef(null);
  const containerRef = useRef(null);
  const touchStartX = useRef(null);
  const autoRef = useRef(null);

  const total = categories.length;

  const loopedCategories = total > 0 ? [...categories, ...categories] : [];

  // ---------- Scroll Helpers ----------
  const getScrollStep = () => {
    if (!containerRef.current || !wrapperRef.current) return 0;

    const container = containerRef.current;
    const wrapper = wrapperRef.current;

    const firstCard = container.querySelector(`.${styles.card}`);
    if (!firstCard) return 0;

    const cardWidth = firstCard.offsetWidth;
    const gap = parseInt(window.getComputedStyle(container).gap || 0);
    const visibleWidth = wrapper.offsetWidth;

    const fullCardWidth = cardWidth + gap;
    const visibleCards = Math.floor(visibleWidth / fullCardWidth);

    return visibleCards * fullCardWidth;
  };

  const scrollNext = () => {
    containerRef.current?.scrollBy({
      left: getScrollStep(),
      behavior: "smooth",
    });
  };

  const scrollPrev = () => {
    containerRef.current?.scrollBy({
      left: -getScrollStep(),
      behavior: "smooth",
    });
  };

  // ---------- Auto Scroll ----------
  useEffect(() => {
    if (isPaused || total <= 3) return;

    autoRef.current = setInterval(scrollNext, AUTO_SLIDE_INTERVAL);

    return () => clearInterval(autoRef.current);
  }, [isPaused, total]);

  // ---------- Infinite Loop ----------
  useEffect(() => {
    const container = containerRef.current;
    if (!container || total === 0) return;

    const handleScroll = () => {
      const scrollWidth = container.scrollWidth;
      const scrollLeft = container.scrollLeft;
      const half = scrollWidth / 2;

      if (scrollLeft >= half) {
        container.scrollLeft = scrollLeft - half;
      }

      if (scrollLeft < 0) {
        container.scrollLeft = half + scrollLeft;
      }
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [total]);

  // ---------- Subcategory Auto Slide ----------
  useEffect(() => {
    if (!categories.length) return;

    const interval = setInterval(() => {
      setSubIndexes((prev) => {
        const updated = { ...prev };

        categories.forEach((cat) => {
          const current = updated[cat.categoryId] || 0;
          const subLen = cat.SubCategory?.length || 0;
          if (!subLen) return;

          updated[cat.categoryId] = current + 1 >= subLen ? 0 : current + 1;
        });

        return updated;
      });
    }, SUB_SLIDE_INTERVAL);

    return () => clearInterval(interval);
  }, [categories]);

  // ---------- Swipe ----------
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (!touchStartX.current) return;

    const diff = touchStartX.current - e.changedTouches[0].clientX;

    if (diff > 50) scrollNext();
    if (diff < -50) scrollPrev();

    touchStartX.current = null;
  };

  if (!categories.length) return null;

  return (
    <div
      className={styles.carouselWrapper}
      ref={wrapperRef}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {total >= 3 && (
        <>
          <button
            className={`${styles.nav} ${styles.left}`}
            onClick={scrollPrev}
          >
            ‹
          </button>
          <button
            className={`${styles.nav} ${styles.right}`}
            onClick={scrollNext}
          >
            ›
          </button>
        </>
      )}

      <div className={styles.carouselContainer} ref={containerRef}>
        {loopedCategories.map((category, idx) => {
          const subIndex = subIndexes[category.categoryId] || 0;

          return (
            <div
              key={`${category.categoryId}-${idx}`}
              className={styles.card}
              onClick={() => {
                const activeSub = category.SubCategory?.[subIndex];
                if (activeSub) {
                  setSearchTerm(activeSub.name);
                }
              }}
            >
              <h2>{category.name}</h2>

              {category.SubCategory?.length > 0 && (
                <div className={styles.subCarouselWrapper}>
                  <div
                    className={styles.subTrack}
                    style={{
                      transform: `translateX(-${subIndex * 100}%)`,
                    }}
                  >
                    {category.SubCategory.map((sub) => {
                      // ✅ Normalize media like main Carousel
                      const validMedia =
                        (sub.media || [])
                          .filter(
                            (item) =>
                              item && (item.mediaThumb || item.mediaUrl),
                          )
                          .map((item) => {
                            const baseUrl =
                              item.mediaThumb &&
                              typeof item.mediaThumb === "string"
                                ? item.mediaThumb
                                : item.mediaUrl;

                            return {
                              ...item,
                              mediaThumb: baseUrl,
                            };
                          }) || [];

                      const firstMedia = validMedia[0];

                      return (
                        <div key={sub._id} className={styles.subSlide}>
                          <h4 className={styles.subTitle}>{sub.name}</h4>

                          {firstMedia && (
                            <div
                              style={{
                                border: "1px solid #ccc",
                                height: "18rem",
                                borderRadius: "10px",
                                overflow: "hidden",
                              }}
                            >
                              <ImageLoader
                                media={firstMedia}
                                alt={sub.name}
                                imgRes={imgRes}
                                zoomImgRes={zoomImgRes}
                                zoom={false}
                                skeletonHeight="100%"
                                skeletonWidth="100%"
                                imageStyle={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
